import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'primereact/multiselect';
import Loader from '../loader/Loader';
import DropdownItem from '../../dropdown-item/DropdownItem';
import { KeywordResponse } from '../../../models/monitor/KeywordResponse';
import { TagsResponse } from '../../../models/tags/TagsResponse';
import { getTagLabel } from '../../../helpers/tags/TagsHelper';
import { useGetProjectOptionsKeywords } from '../../../api/hooks/project/options/useGetProjectOptionsKeywords';
import { TAG_NAME } from '../../../utils/urls';

interface ChangeProjectProps {
  projectId: number;
  projectName: string;
  keywords: KeywordResponse[];
  tags: TagsResponse[];
  onChangeProject: (selectedKeywords: string[]) => void;
  // Tag-resolved keys of "live" brands (data under current models). When provided, orphan brands are
  // hidden from the options — except ones the user already selected (so existing picks aren't lost).
  liveKeys?: string[];
  // Project default brand keyword (raw or tag key). Kept selectable even when orphaned — when the
  // current brand differs from the default, the default must still be pickable for analysis.
  defaultKeyword?: string;
}

const ChooseKeywords: React.FC<ChangeProjectProps> = ({
  projectId,
  projectName,
  keywords,
  tags,
  onChangeProject,
  liveKeys,
  defaultKeyword,
}) => {
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<{ name: string; type: string }[] | null>(null);
  const [selectedRawKeys, setSelectedRawKeys] = useState<Set<string>>(new Set());
  const { fetchProjectOptionsKeywords } = useGetProjectOptionsKeywords();

  useEffect(() => {
    void prepareCurrentData();
  }, []);

  const buildOptions = (selectedSet: Set<string>) => {
    const liveSet = new Set(liveKeys ?? []);
    // Only filter when we actually have a live set (avoid hiding everything during load).
    const applyLiveFilter = liveSet.size > 0;
    const options = keywords
      .filter((keyword) => keyword.normalizedKeyword !== projectName)
      .filter((keyword) => {
        if (!applyLiveFilter) {
          return true;
        }
        const resolvedKey = keyword.tagId ? `${TAG_NAME}${keyword.tagId}` : keyword.normalizedKeyword;
        return liveSet.has(resolvedKey) || selectedSet.has(keyword.normalizedKeyword);
      })
      .map((keyword) => ({
        name: keyword.tagId
          ? getTagLabel(tags, keyword.tagId, keyword.normalizedKeyword)
          : keyword.keyword,
        type: keyword.normalizedKeyword,
      }));

    // Keep the project default brand selectable even when it's orphaned (not live) — so when the
    // current brand differs from the default, you can still pick the default for analysis. Skip it
    // when it IS the current brand (own brand is always excluded). Resolve to the tag key + label
    // when the default belongs to a tag (the live set / options key tagged brands by their tag key).
    if (defaultKeyword) {
      let optionType = defaultKeyword;
      let optionName = defaultKeyword;
      if (defaultKeyword.startsWith(TAG_NAME)) {
        optionName = getTagLabel(tags, Number(defaultKeyword.slice(TAG_NAME.length)), defaultKeyword);
      } else {
        const tag = tags.find((tr) => tr.keywordsData?.some((kd) => kd.normalizedKeyword === defaultKeyword));
        if (tag) {
          optionType = `${TAG_NAME}${tag.id}`;
          optionName = getTagLabel(tags, tag.id, defaultKeyword);
        }
      }
      if (optionType !== projectName && !options.some((option) => option.type === optionType)) {
        options.push({ name: optionName, type: optionType });
      }
    }

    return options;
  };

  const prepareOptions = () => buildOptions(selectedRawKeys);

  const prepareCurrentData = async () => {
    const projectOptionsKeywords = await fetchProjectOptionsKeywords({ projectId });
    const selectedSet = new Set(projectOptionsKeywords);
    setSelectedRawKeys(selectedSet);

    const allOptions = buildOptions(selectedSet);
    const selected = allOptions.filter(
      (item) => selectedSet.has(item.type) && item.name !== projectName
    );

    setSelectedItems(selected);
    // Seed the parent's selection ref with the current value so saving without touching the
    // MultiSelect keeps the existing brands instead of overwriting them with an empty list.
    onChangeProject(selected.map((item) => item.type));
  };

  const handleChange = (e: any) => {
    const updated = e.value || [];
    const withoutProjectName = updated.filter((item: any) => item.normalizedKeyword !== projectName);
    const finalSelection = [...withoutProjectName];
    setSelectedItems(withoutProjectName);
    onChangeProject(finalSelection.map((item) => item.type));
  };

  const isOptionDisabled = (option: { type: string }) => option.type === projectName;

  if (!selectedItems) {
    return <Loader />;
  }

  return (
    <div>
      <MultiSelect
        value={selectedItems}
        onChange={handleChange}
        options={prepareOptions()}
        className="dropdown dropdown--multi-select"
        optionLabel="name"
        optionDisabled={isOptionDisabled}
        itemTemplate={(option) => <DropdownItem name={option.name} className="capitalize" />}
        selectedItemTemplate={(option) =>
          option ? <span className="capitalize">{option.name}</span> : option
        }
        maxSelectedLabels={1}
        selectionLimit={13}
        showSelectAll={false}
        placeholder={t('selectBrands')}
        filter
      />
    </div>
  );
};

export default ChooseKeywords;